import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import heroPhoto from '../../assets/images_of_victoria/IMG_2250.jpg';
import carouselPhotoOne from '../../assets/for-carousel/1.jpeg';
import carouselPhotoTwo from '../../assets/for-carousel/10-1-scaled.jpg';
import carouselPhotoThree from '../../assets/for-carousel/20200222-116-scaled.jpg';
import carouselPhotoFour from '../../assets/for-carousel/20200222-14-2.jpg';
import carouselPhotoFive from '../../assets/for-carousel/20200315_101423.jpg';
import carouselPhotoSix from '../../assets/for-carousel/61-1.jpg';
import carouselPhotoSeven from '../../assets/for-carousel/ARI-CALI.jpg';
import carouselPhotoEight from '../../assets/for-carousel/ARIANA-CHARLESTON.jpg';
import carouselPhotoNine from '../../assets/for-carousel/ARIANA-GAMEC0CKS.jpg';
import carouselPhotoTen from '../../assets/for-carousel/IMG_2273.jpg';
import carouselPhotoEleven from '../../assets/for-carousel/IMG_7464-1.jpg';
import carouselPhotoTwelve from '../../assets/for-carousel/IMG_7488-scaled.jpg';
import carouselPhotoThirteen from '../../assets/for-carousel/IMG_8113-1.jpg';
import carouselPhotoFourteen from '../../assets/for-carousel/IMG_8356.jpg';
import communityPhoto from '../../assets/stock_photos/ChatGPT Image Mar 23, 2026, 06_05_10 PM.png';
import programPhoto from '../../assets/stock_photos/ChatGPT Image Mar 23, 2026, 06_05_42 PM.png';
import heroBackground from '../../assets/bg-image.png';

const heroVideoEmbedUrl =
  'https://www.youtube-nocookie.com/embed/zlfgLXqvx_M?autoplay=1&mute=1&controls=0&loop=1&playlist=zlfgLXqvx_M&playsinline=1&modestbranding=1&rel=0';

const missionPoints = [
  {
    title: 'Parents, community, family',
    description: 'Victoria\'s Place is rooted in belonging, support, and shared care for every child.',
  },
  {
    title: 'Columbia, South Carolina',
    description: 'We are here to serve children with Down Syndrome in our local community.',
  },
  {
    title: 'Positive outlets',
    description: 'Programs in art, music, sports, fine motor skills, and reading help children express themselves.',
  },
];

const programCards = [
  {
    number: '01',
    title: 'Arts',
    description:
      'Art develops eye-hand coordination, visual awareness, confidence, and creativity in ways children can see and feel.',
  },
  {
    number: '02',
    title: 'Music',
    description:
      'Music supports memory, coordination, cognitive growth, social development, and self-esteem.',
  },
  {
    number: '03',
    title: 'Sports',
    description:
      'Sports help build muscle tone, confidence, and step-by-step learning through movement and play.',
  },
  {
    number: '04',
    title: 'Fine Motor Skills',
    description:
      'Hands-on activities using small muscle groups help strengthen coordination, flexibility, and daily development.',
  },
  {
    number: '05',
    title: 'Reading',
    description:
      'Reading activities help build vocabulary, spoken language, imagination, and working memory in enjoyable ways.',
  },
];

const supportWays = [
  {
    title: 'Donate',
    description: 'Help keep programs, supplies, and family support steady and accessible.',
    cta: 'Give today',
    path: '/give',
  },
  {
    title: 'Volunteer',
    description: 'Share your time, encouragement, and consistency with the children we serve.',
    cta: 'Reach out',
    path: '/contact',
  },
  {
    title: 'Connect',
    description: 'Families, partners, and supporters are welcome to learn more about the mission.',
    cta: 'Contact us',
    path: '/contact',
  },
];

const founderProof = {
  quote:
    'As a mother, I knew I had to do more for Victoria. Her smile lights up a room and brings so much joy to people.',
  attribution: 'Dr. Melanie Ray, Founder',
  support:
    "Victoria's Place exists so that same joy, belonging, and opportunity can be shared with other children and families.",
};

const carouselImages = [
  {
    src: carouselPhotoOne,
    alt: "Victoria's Place carousel photo 1",
  },
  {
    src: carouselPhotoTwo,
    alt: "Victoria's Place carousel photo 2",
  },
  {
    src: carouselPhotoThree,
    alt: "Victoria's Place carousel photo 3",
  },
  {
    src: carouselPhotoFour,
    alt: "Victoria's Place carousel photo 4",
  },
  {
    src: carouselPhotoFive,
    alt: "Victoria's Place carousel photo 5",
  },
  {
    src: carouselPhotoSix,
    alt: "Victoria's Place carousel photo 6",
  },
  {
    src: carouselPhotoSeven,
    alt: "Victoria's Place carousel photo 7",
  },
  {
    src: carouselPhotoEight,
    alt: "Victoria's Place carousel photo 8",
  },
  {
    src: carouselPhotoNine,
    alt: "Victoria's Place carousel photo 9",
  },
  {
    src: carouselPhotoTen,
    alt: "Victoria's Place carousel photo 10",
  },
  {
    src: carouselPhotoEleven,
    alt: "Victoria's Place carousel photo 11",
  },
  {
    src: carouselPhotoTwelve,
    alt: "Victoria's Place carousel photo 12",
  },
  {
    src: carouselPhotoThirteen,
    alt: "Victoria's Place carousel photo 13",
  },
  {
    src: carouselPhotoFourteen,
    alt: "Victoria's Place carousel photo 14",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.45 },
};

export default function Home() {
  return (
    <div className="home-page">
      <section
        className="home-bleed home-hero"
        style={{ '--home-hero-background-image': `url(${heroBackground})` } as CSSProperties}
      >
        <div className="home-hero-image-shell" aria-hidden="true" />
        <div className="home-hero-video-shell" aria-hidden="true">
          <iframe
            className="home-hero-video"
            src={heroVideoEmbedUrl}
            title="Victoria's Place hero background video"
            allow="autoplay; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            tabIndex={-1}
          />
        </div>

        <div className="home-hero-inner">
          <div className="home-hero-copy">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="eyebrow home-hero-eyebrow"
            >
              Victoria&apos;s Place
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.06 }}
              className="home-hero-title"
            >
              The Sky is the Limit
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.12 }}
              className="home-hero-lead"
            >
              You can do anything and be anything you want to be. No worry no hurry at
              Victoria&apos;s Place.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
              className="home-hero-support"
            >
              We are parents, we are a community, and we are family. Based in Columbia, South
              Carolina, Victoria&apos;s Place exists to help children with Down Syndrome grow through
              encouragement, creativity, and consistent support.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.24 }}
              className="home-hero-actions"
            >
              <Link to="/give" className="button-primary tap-target">
                Donate
              </Link>
              <Link to="/programs" className="button-secondary tap-target">
                Explore programs
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="home-hero-strip"
            >
              <span>Faith-rooted care</span>
              <span>Children with Down Syndrome</span>
              <span>Columbia, SC</span>
            </motion.div>
          </div>
        </div>
      </section>

      <motion.section
        {...fadeUp}
        className="home-bleed home-carousel-section"
        aria-label="Victoria's Place photo gallery"
      >
        <div className="home-carousel-marquee">
          <motion.div
            className="home-carousel-track"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 32, ease: 'linear', repeat: Infinity, repeatType: 'loop' }}
          >
            <div className="home-carousel-group">
              {carouselImages.map((image, index) => (
                <figure key={`primary-${index}`} className="home-carousel-card">
                  <img src={image.src} alt={image.alt} />
                </figure>
              ))}
            </div>

            <div className="home-carousel-group" aria-hidden="true">
              {carouselImages.map((image, index) => (
                <figure key={`duplicate-${index}`} className="home-carousel-card">
                  <img src={image.src} alt="" />
                </figure>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      <div className="page-shell home-page-shell space-y-10 sm:space-y-12">

        <motion.section
          {...fadeUp}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="home-proof-band"
        >
          <div className="home-proof-band-copy">
            <span className="home-section-label">Founder's heart</span>
            <h2>{founderProof.quote}</h2>
            <p>{founderProof.support}</p>
          </div>

          <div className="home-proof-band-meta">
            <span className="home-proof-band-line" aria-hidden="true" />
            <strong>{founderProof.attribution}</strong>
            <span>Faith-rooted care. Real family story. Real local impact.</span>
          </div>
        </motion.section>

        <section className="home-mission-band">
          {missionPoints.map((point, index) => (
            <motion.article
              key={point.title}
              {...fadeUp}
              transition={{ duration: 0.45, delay: index * 0.07 }}
              className="home-mission-item"
            >
              <p className="home-section-label">Mission focus</p>
              <h2>{point.title}</h2>
              <p>{point.description}</p>
            </motion.article>
          ))}
        </section>

        <section className="home-story-grid">
          <motion.article {...fadeUp} className="home-story-panel">
            <span className="home-section-label">Who we are</span>
            <h2>We are parents, community, and family.</h2>
            <p>
              Victoria&apos;s Place is here to help serve children with Down Syndrome, based in
              Columbia, South Carolina. Our goal is to help make this world a better place for
              children with Down Syndrome through the programs we offer.
            </p>
            <p>
              We believe in giving children with Down Syndrome a chance to express themselves by
              giving them a positive outlet through activities such as art, music, sports, fine
              motor skills, and reading.
            </p>

            <div className="home-story-program-panel">
              <p className="home-story-program-kicker">A welcoming place for expression</p>
              <h3>Children deserve room to grow and be seen.</h3>
              <div className="home-story-token-row">
                <span>Art</span>
                <span>Music</span>
                <span>Sports</span>
                <span>Reading</span>
                <span>Motor Skills</span>
              </div>
              <div className="home-story-help-note">
                <strong>How we help</strong>
                <span>Positive outlets that build confidence, coordination, and connection.</span>
              </div>
            </div>

            <Link to="/about-us" className="button-primary tap-target w-fit">
              Read more
            </Link>
          </motion.article>

          <motion.aside
            {...fadeUp}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="home-story-aside"
          >
            <figure className="home-story-photo">
              <img
                src={heroPhoto}
                alt="Victoria smiling outdoors in a colorful dress"
              />
            </figure>

            <figure className="home-story-photo home-story-photo--support">
              <img
                src={communityPhoto}
                alt="A joyful family and children gathered together over art activities"
              />
            </figure>

            <div className="home-quote-panel">
              <span className="quote-mark">&quot;</span>
              <p>No worry no hurry at Victoria&apos;s Place.</p>
              <span>That atmosphere is part of the mission.</span>
            </div>

            <div className="home-values-list">
              <div className="home-value-item">
                <strong>Encouragement first</strong>
                <p>Children should feel welcomed, not rushed.</p>
              </div>
              <div className="home-value-item">
                <strong>Programs with purpose</strong>
                <p>Each activity is meant to strengthen expression, growth, and confidence.</p>
              </div>
              <div className="home-value-item">
                <strong>Support for families</strong>
                <p>Community matters just as much as programming.</p>
              </div>
            </div>
          </motion.aside>
        </section>

        <section className="home-program-section">
          <motion.div {...fadeUp} className="home-program-heading">
            <span className="home-section-label">Program services</span>
            <h2>Positive outlets designed to build confidence and expression.</h2>
            <p>
              The programs at Victoria&apos;s Place are meant to help children strengthen
              coordination, creativity, communication, and self-confidence through supportive,
              engaging experiences.
            </p>

            <figure className="home-program-feature">
              <img
                src={programPhoto}
                alt="Children painting together during an art activity"
              />
              <figcaption>Creative activities help children build expression, focus, and confidence.</figcaption>
            </figure>
          </motion.div>

          <div className="home-program-grid">
            {programCards.map((program, index) => (
              <motion.article
                key={program.title}
                {...fadeUp}
                transition={{ duration: 0.42, delay: index * 0.06 }}
                className="home-program-card"
              >
                <span className="home-program-number">{program.number}</span>
                <h3>{program.title}</h3>
                <p>{program.description}</p>
              </motion.article>
            ))}
          </div>
        </section>

        <motion.section
          {...fadeUp}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="home-support-section"
        >
          <div className="home-support-copy">
            <span className="home-section-label home-section-label--light">Support us</span>
            <h2>Support us and change the course of a child&apos;s life today.</h2>
            <p>
              Your generosity helps keep Victoria&apos;s Place welcoming, practical, and ready to
              serve children and families with care.
            </p>
          </div>

          <div className="home-support-grid">
            {supportWays.map((item) => (
              <article key={item.title} className="home-support-card">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <Link to={item.path} className="button-secondary tap-target">
                  {item.cta}
                </Link>
              </article>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
}
